import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CalendarRange, ChevronLeft, ChevronRight, Loader2, Clock, CalendarDays, Plane, Zap, AlertTriangle } from "lucide-react";
import { CalendarDayDetail } from "../components/CalendarDayDetail";
import { fmtH } from "../lib/planning";

const DAY_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const selectCls = "h-8 max-w-[260px] rounded-md border border-slate-200 bg-white px-2 text-xs";

const Chip = ({ icon: Icon, cls, children, testId, wrap }) => (
    <div className={`flex items-start gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${wrap ? "" : "truncate"} ${cls}`} data-testid={testId}>
        <Icon className="mt-px h-3 w-3 shrink-0" /><span className={wrap ? "break-words" : "truncate"}>{children}</span>
    </div>
);

const Kpi = ({ icon: Icon, label, value, sub, tone, testId }) => (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid={testId}>
        <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide ${tone}`}><Icon className="h-3.5 w-3.5" /> {label}</div>
        <div className="font-display text-xl font-bold text-slate-800">{value}</div>
        {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
);

const DayCell = ({ day, single, isToday, selected, onClick }) => {
    const t = day.totals;
    const empty = !t.timbrature && !day.programmati.length && !t.assenze && !t.straordinari;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`cal-day-${day.date}`}
            className={`flex min-h-[104px] flex-col gap-1 rounded-md border p-1.5 text-left transition-colors ${
                selected ? "border-[#376DB3] bg-[#376DB3]/5 ring-1 ring-[#376DB3]" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
            } ${day.dow >= 6 && !selected ? "bg-slate-50/70" : ""}`}
        >
            <div className="flex items-center justify-between">
                <span className={`font-mono-numbers text-xs font-bold ${isToday ? "rounded-full bg-[#376DB3] px-1.5 text-white" : "text-slate-600"}`}>{Number(day.date.slice(8))}</span>
                {t.anomalie > 0 && <AlertTriangle className="h-3 w-3 text-red-500" title={`${t.anomalie} anomalie timbratura`} />}
            </div>
            {empty ? <span className="text-[10px] text-slate-300">—</span> : single ? (
                <>
                    {day.programmati.map((p) => (
                        <Chip key={`${p.employee_id}-${p.start}-${p.worksite_id}-${p.status}`} wrap icon={CalendarDays} cls={p.status === "assente" ? "bg-red-50 text-red-500 line-through" : p.status === "sostituto" ? "bg-sky-50 text-sky-700" : "bg-[#376DB3]/10 text-[#376DB3]"}>
                            {p.start}–{p.end} {p.worksite_name}
                        </Chip>
                    ))}
                    {day.timbrature.map((x) => (
                        <Chip key={x.employee_id} wrap icon={Clock} cls={t.anomalie ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}>
                            {x.stamps.map((s) => `${s.type === "in" ? "E" : "U"} ${s.time}`).join(" · ")} · {fmtH(x.ore)}h
                        </Chip>
                    ))}
                    {day.assenze.map((a) => <Chip key={`${a.employee_id}-${a.type}-${a.source}`} wrap icon={Plane} cls="bg-red-50 text-red-600">{a.label}</Chip>)}
                    {day.straordinari.map((x) => <Chip key={x.id} wrap icon={Zap} cls="bg-violet-50 text-violet-700">{fmtH(x.hours)}h {x.description}</Chip>)}
                </>
            ) : (
                <>
                    {t.timbrature > 0 && <Chip icon={Clock} cls={t.anomalie ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}>{t.timbrature} timbr. · {fmtH(t.ore_timbrate)}h</Chip>}
                    {day.programmati.length > 0 && t.programmati > 0 && <Chip icon={CalendarDays} cls={t.scoperti ? "bg-amber-50 text-amber-700" : "bg-[#376DB3]/10 text-[#376DB3]"}>{t.programmati} turni · {fmtH(t.ore_programmate)}h{t.scoperti ? ` · ${t.scoperti} scop.` : ""}</Chip>}
                    {t.programmati === 0 && t.scoperti > 0 && <Chip icon={AlertTriangle} cls="bg-amber-50 text-amber-700">{t.scoperti} turn{t.scoperti === 1 ? "o scoperto" : "i scoperti"}</Chip>}
                    {t.assenze > 0 && <Chip icon={Plane} cls="bg-red-50 text-red-600">{t.assenze} assen{t.assenze === 1 ? "za" : "ze"}</Chip>}
                    {t.straordinari > 0 && <Chip icon={Zap} cls="bg-violet-50 text-violet-700">{t.straordinari} straord. · {fmtH(t.ore_straordinari)}h</Chip>}
                </>
            )}
        </button>
    );
};

export default function CalendarioPresenzePage() {
    const now = new Date();
    const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
    const [employeeId, setEmployeeId] = useState("");
    const [employees, setEmployees] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        api.get("/presenze/planning/employees").then(({ data }) => setEmployees(data)).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/presenze/calendar", { params: { year: ym.year, month: ym.month, employee_id: employeeId || undefined } });
            setData(data);
        } catch {
            toast.error("Errore nel caricamento del calendario");
        } finally { setLoading(false); }
    }, [ym, employeeId]);

    useEffect(() => { load(); setSelected(null); }, [load]);

    const shift = (n) => setYm(({ year, month }) => {
        const d = new Date(year, month - 1 + n, 1);
        return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });

    const selectedDay = useMemo(() => data?.days.find((d) => d.date === selected) || null, [data, selected]);
    const leading = data ? data.days[0].dow - 1 : 0;
    const tot = data?.totals || {};

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="calendario-page">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <CalendarRange className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Calendario presenze</h1>
                        <p className="text-sm text-slate-500">Quadro mensile: timbrature, turni assegnati, assenze e straordinari</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={selectCls} data-testid="cal-employee-select">
                        <option value="">Tutti i dipendenti</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.cognome} {e.nome}{e.matricola ? ` · ${e.matricola}` : ""}</option>)}
                    </select>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setYm({ year: now.getFullYear(), month: now.getMonth() + 1 })} data-testid="cal-today">Oggi</Button>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
            </div>

            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div className="font-display text-sm font-bold capitalize text-slate-700" data-testid="cal-month-label">
                    {MONTHS[ym.month - 1]} {ym.year}{data?.employee ? ` — ${data.employee.name}` : ""}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-emerald-600" /> Timbrature</span>
                    <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3 text-[#376DB3]" /> Turni assegnati</span>
                    <span className="flex items-center gap-1"><Plane className="h-3 w-3 text-red-500" /> Assenze</span>
                    <span className="flex items-center gap-1"><Zap className="h-3 w-3 text-violet-600" /> Straordinari</span>
                    <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-red-500" /> Anomalie</span>
                </div>
            </div>

            {data && (
                <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="cal-kpis">
                    <Kpi icon={Clock} label="Ore timbrate" value={`${fmtH(tot.ore_timbrate || 0)}h`} sub={`${tot.timbrature || 0} presenze giornaliere`} tone="text-emerald-700" testId="kpi-timbrate" />
                    <Kpi icon={CalendarDays} label="Turni assegnati" value={tot.programmati || 0} sub={`${fmtH(tot.ore_programmate || 0)}h programmate`} tone="text-[#376DB3]" testId="kpi-programmati" />
                    <Kpi icon={AlertTriangle} label="Turni scoperti" value={tot.scoperti || 0} sub="assenze senza sostituto" tone="text-amber-700" testId="kpi-scoperti" />
                    <Kpi icon={Plane} label="Assenze" value={tot.assenze || 0} sub="giorni-persona (ferie, permessi…)" tone="text-red-600" testId="kpi-assenze" />
                    <Kpi icon={Zap} label="Straordinari" value={`${fmtH(tot.ore_straordinari || 0)}h`} sub={`${tot.straordinari || 0} interventi`} tone="text-violet-700" testId="kpi-straordinari" />
                </div>
            )}

            <div className={`grid gap-4 ${selectedDay ? "lg:grid-cols-[1fr_360px]" : ""}`}>
                {loading || !data ? (
                    <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
                ) : (
                    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="cal-grid">
                        <div className="mb-1 grid grid-cols-7 gap-1.5">
                            {DAY_SHORT.map((d) => <div key={d} className="px-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">{d}</div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1.5">
                            {Array.from({ length: leading }).map((_, i) => <div key={`b${i}`} />)}
                            {data.days.map((day) => (
                                <DayCell key={day.date} day={day} single={!!employeeId} isToday={day.date === data.today}
                                    selected={selected === day.date} onClick={() => setSelected(selected === day.date ? null : day.date)} />
                            ))}
                        </div>
                    </div>
                )}
                {selectedDay && <CalendarDayDetail day={selectedDay} onClose={() => setSelected(null)} />}
            </div>
        </div>
    );
}
